/**
 * Address autocomplete input with optional Photon suggestions.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Loader2 } from "lucide-react";
import {
  GeocodingSuggestion,
  searchAddress,
} from "@/services/geocoding.service";
import { formatRegionPath } from "@/utils/regionLabel";

export type AddressAutocompleteStatus =
  | "idle"
  | "no_results"
  | "unavailable"
  | "invalid_response";

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (suggestion: GeocodingSuggestion) => void;
  onStatusChange?: (status: AddressAutocompleteStatus) => void;
  t: (key: string) => string;
  inputId?: string;
  disabled?: boolean;
  suggestionsEnabled?: boolean;
}

const MIN_QUERY_LEN = 3;
const DEBOUNCE_MS = 40;
const MIN_REQUEST_INTERVAL_MS = 100;

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  onStatusChange,
  t,
  inputId,
  disabled = false,
  suggestionsEnabled = true,
}: AddressAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<GeocodingSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorKind, setErrorKind] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const prevQueryRef = useRef<string>(value);
  const blurTimeoutRef = useRef<number | null>(null);
  const lastRequestAtRef = useRef(0);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      window.clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    if (blurTimeoutRef.current) {
      window.clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = window.setTimeout(() => {
      setIsFocused(false);
    }, 150);
  }, []);

  const handleQueryChange = useCallback(
    (next: string) => {
      setQuery(next);
      onChange(next);
    },
    [onChange]
  );

  useEffect(() => {
    if (disabled || !suggestionsEnabled) {
      setSuggestions([]);
      setErrorKind(null);
      setLoading(false);
      return;
    }

    const trimmed = query.trim();
    const prev = prevQueryRef.current;
    prevQueryRef.current = query;

    if (trimmed.length === 0) {
      setSuggestions([]);
      setErrorKind(null);
      setLoading(false);

      if (prev.trim().length > 0) {
        onStatusChange?.("idle");
      }
      return;
    }

    if (trimmed.length < MIN_QUERY_LEN) {
      setSuggestions([]);
      setErrorKind(null);
      setLoading(false);
      onStatusChange?.("idle");
      return;
    }

    let cancelled = false;
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestAtRef.current;
    const throttleDelay =
      timeSinceLastRequest >= MIN_REQUEST_INTERVAL_MS
        ? 0
        : MIN_REQUEST_INTERVAL_MS - timeSinceLastRequest;
    const waitMs = Math.max(DEBOUNCE_MS, throttleDelay);

    const timer = window.setTimeout(async () => {
      lastRequestAtRef.current = Date.now();
      setLoading(true);
      setErrorKind(null);

      const res = await searchAddress(trimmed);

      if (cancelled) return;
      setLoading(false);

      if (!res.ok) {
        setSuggestions([]);
        setErrorKind(res.kind);
        onStatusChange?.(
          res.kind === "invalid_response" ? "invalid_response" : "unavailable"
        );
        return;
      }

      setSuggestions(res.suggestions);
      setErrorKind(null);

      if (res.suggestions.length === 0) {
        onStatusChange?.("no_results");
      } else {
        onStatusChange?.("idle");
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [disabled, query, onStatusChange]);

  const showList =
    !disabled &&
    suggestionsEnabled &&
    isFocused &&
    query.trim().length >= MIN_QUERY_LEN &&
    (loading || errorKind !== null || suggestions.length > 0);

  const handleSelect = useCallback(
    (suggestion: GeocodingSuggestion) => {
      onSelect(suggestion);
      setSuggestions([]);
      setIsFocused(false);
    },
    [onSelect]
  );

  const secondaryText = (suggestion: GeocodingSuggestion) => {
    const regionPathEntries = formatRegionPath(
      suggestion.region?.pathNames ?? [],
      suggestion.region?.pathNamesEn ?? []
    );
    const regionPath =
      regionPathEntries.length > 0 ? regionPathEntries.join(" > ") : null;
    if (regionPath) return regionPath;

    const parts = [suggestion.postalCode, suggestion.city, suggestion.state].filter(
      Boolean
    );
    return parts.length > 0 ? parts.join(" · ") : "";
  };

  const spinnerKeyframes =
    "@keyframes address-autocomplete-spin { to { transform: rotate(360deg); } }";
  const highlightedItemStyle =
    ".address-autocomplete [data-slot='command-item'][data-selected='true'] {" +
    "background-color: color-mix(in srgb, var(--accent) 14%, var(--background));" +
    "color: color-mix(in srgb, var(--foreground) 88%, var(--surface));" +
    "}";
  const listStyle = {
    backgroundColor: "color-mix(in srgb, var(--background) 80%, var(--surface))",
  } as const;

  return (
    <Command
      shouldFilter={false}
      className="border rounded-md address-autocomplete"
    >
      <style>{`${spinnerKeyframes}\n${highlightedItemStyle}`}</style>
      <CommandInput
        id={inputId}
        placeholder={t("websiteSuggest.addressAutoPlaceholder")}
        value={query}
        onValueChange={handleQueryChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
      />

      {showList && (
        <CommandList style={listStyle}>
          <CommandEmpty>
            {loading ? (
              <div className="flex items-center justify-center gap-2 text-secondary">
                <Loader2
                  className="h-4 w-4"
                  style={{ animation: "address-autocomplete-spin 1s linear infinite" }}
                />
                <span>{t("websiteSuggest.loadingShort")}</span>
              </div>
            ) : errorKind ? (
              t("websiteSuggest.addressAutoUnavailable")
            ) : (
              t("websiteSuggest.addressAutoNoResults")
            )}
          </CommandEmpty>

          <CommandGroup>
            {suggestions.map((suggestion) => {
              const secondary = secondaryText(suggestion);
              return (
                <CommandItem
                  key={suggestion.id}
                  value={suggestion.label}
                  onSelect={() => handleSelect(suggestion)}
                >
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="truncate">{suggestion.label}</span>
                    {secondary && (
                      <span className="text-xs text-secondary truncate">
                        {secondary}
                      </span>
                    )}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      )}
    </Command>
  );
}
